import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Menu, X, Sparkles, ChevronDown, MessageCircle } from 'lucide-react';
import Popup from './Popup';
import MegaMenu from './MegaMenu';
import { mockData } from '../mock';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isActive = (path) => location.pathname === path;

  return (
    <>
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled 
          ? 'bg-primary/95 backdrop-blur-lg shadow-2xl' 
          : 'bg-primary shadow-md'
      }`}>
        
        <nav className="container mx-auto px-6 py-4 relative">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center group">
              <img src="/logo-white.png" alt="Global Trademark Federation" className="h-12 md:h-14 group-hover:scale-105 transition-transform duration-300" />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-2">
              <Link 
                to="/" 
                className={`relative font-semibold px-4 py-2 rounded-lg transition-all duration-300 ${
                  isActive('/') 
                    ? 'text-white bg-secondary/20' 
                    : 'text-white/80 hover:text-white hover:bg-white/10'
                }`}
              >
                Home
                {isActive('/') && (
                  <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-6 h-0.5 bg-secondary rounded-full" />
                )}
              </Link>
              <Link 
                to="/about" 
                className={`relative font-semibold px-4 py-2 rounded-lg transition-all duration-300 ${
                  isActive('/about') 
                    ? 'text-white bg-secondary/20' 
                    : 'text-white/80 hover:text-white hover:bg-white/10'
                }`}
              >
                About
                {isActive('/about') && (
                  <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-6 h-0.5 bg-secondary rounded-full" />
                )}
              </Link>
              
              {/* Mega Menu */}
              <MegaMenu />
              
              <Link 
                to="/contact" 
                className={`relative font-semibold px-4 py-2 rounded-lg transition-all duration-300 ${
                  isActive('/contact') 
                    ? 'text-white bg-secondary/20' 
                    : 'text-white/80 hover:text-white hover:bg-white/10'
                }`}
              >
                Contact Us
                {isActive('/contact') && (
                  <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-6 h-0.5 bg-secondary rounded-full" />
                )}
              </Link>
            </div>

            {/* Enhanced CTA Buttons */}
            <div className="hidden lg:flex items-center gap-3">
              <Button 
                onClick={() => setIsPopupOpen(true)} 
                className="bg-secondary hover:bg-secondary/90 text-white font-bold px-8 py-2.5 rounded-full shadow-lg hover:shadow-secondary/50 transition-all hover:scale-110 duration-300 group relative overflow-hidden"
              >
                <span className="relative z-10 flex items-center">
                  <Sparkles className="w-4 h-4 mr-2 group-hover:rotate-12 transition-transform" />
                  Register My Trademark
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
              </Button>
              <Button 
                onClick={() => { if (window.LiveChatWidget) window.LiveChatWidget.call('maximize'); }} 
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-primary font-bold px-6 py-2.5 rounded-full transition-all hover:scale-105 duration-300"
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                Chat Now
              </Button>
            </div>

            {/* Mobile CTA + Menu Button */}
            <div className="lg:hidden flex items-center gap-2">
              <Button
                data-testid="mobile-chat-btn"
                onClick={() => { /* Chat script will be integrated here */ }}
                variant="outline"
                className="border border-white text-white hover:bg-white hover:text-primary font-bold text-xs px-3 py-2 rounded-full transition-all"
              >
                <MessageCircle className="w-3 h-3 mr-1 inline" />
                Chat Now
              </Button>
              <button
                className="text-white p-2 hover:bg-white/10 rounded-lg transition-all"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
              >
                {isMenuOpen ? <X size={24} className="animate-spin" /> : <Menu size={24} />}
              </button>
            </div>
          </div>

          {/* Enhanced Mobile Menu */}
          {isMenuOpen && (
            <div className="lg:hidden fixed top-[72px] left-0 right-0 bottom-0 bg-primary/98 backdrop-blur-md overflow-y-auto z-50">
              <div className="p-4 space-y-2">
              <Link 
                to="/" 
                className={`block py-3 px-4 rounded-lg font-semibold transition-all ${
                  isActive('/') 
                    ? 'bg-secondary text-white' 
                    : 'text-white hover:bg-white/10'
                }`}
                onClick={() => setIsMenuOpen(false)}
              >
                Home
              </Link>
              <Link 
                to="/about" 
                className={`block py-3 px-4 rounded-lg font-semibold transition-all ${
                  isActive('/about') 
                    ? 'bg-secondary text-white' 
                    : 'text-white hover:bg-white/10'
                }`}
                onClick={() => setIsMenuOpen(false)}
              >
                About
              </Link>
              <div className="space-y-2">
                <button
                  onClick={() => setIsServicesOpen(!isServicesOpen)}
                  className="w-full flex items-center justify-between font-semibold text-white px-4 py-3 rounded-lg hover:bg-white/10 transition-all"
                >
                  Services
                  <ChevronDown className={`w-4 h-4 transition-transform ${isServicesOpen ? 'rotate-180' : ''}`} />
                </button>
                {isServicesOpen && (
                  <div className="space-y-1 pl-4">
                    {mockData.serviceCategories.map((category) => (
                      <div key={category.id} className="space-y-1">
                        <div className="text-xs font-bold text-secondary px-4 py-2 uppercase tracking-wider">
                          {category.name}
                        </div>
                        {category.services.slice(0, 3).map((serviceSlug) => {
                          const service = mockData.services.find(s => s.slug === serviceSlug);
                          if (!service) return null;
                          return (
                            <Link
                              key={service.slug}
                              to={`/services/${service.slug}`}
                              className="block py-2 pl-8 pr-4 text-sm text-white/90 hover:text-secondary hover:bg-white/5 rounded-lg transition-all"
                              onClick={() => setIsMenuOpen(false)}
                            >
                              • {service.title}
                            </Link>
                          );
                        })}
                        {category.services.length > 3 && (
                          <div className="pl-8 text-xs text-white/60 py-1">
                            +{category.services.length - 3} more
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <Link 
                to="/contact" 
                className={`block py-3 px-4 rounded-lg font-semibold transition-all ${
                  isActive('/contact') 
                    ? 'bg-secondary text-white' 
                    : 'text-white hover:bg-white/10'
                }`}
                onClick={() => setIsMenuOpen(false)}
              >
                Contact Us
              </Link>
              <Button 
                onClick={() => { setIsPopupOpen(true); setIsMenuOpen(false); }} 
                className="bg-secondary hover:bg-secondary/90 text-white font-bold w-full mt-4 rounded-full shadow-lg hover:shadow-secondary/50 transition-all hover:scale-105"
              >
                <Sparkles className="w-4 h-4 mr-2 inline" />
                Register My Trademark
              </Button>
              </div>
            </div>
          )}
        </nav>
      </header>
      
      <Popup isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)} />
    </>
  );
};

export default Header;